import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { alpha, createTheme, ThemeProvider } from '@mui/material/styles';

export const COLOR_MODE_STORAGE_KEY = 'morty-color-mode';
export const COLOR_MODE_OVERRIDE_STORAGE_KEY = 'morty-color-mode-user-set';

const LIGHT_MODE_START_MINUTES = (5 * 60) + 30;
const DARK_MODE_START_MINUTES = 19 * 60;

const ColorModeContext = createContext(null);

export const getScheduledColorMode = (date = new Date()) => {
  const minutesSinceMidnight = (date.getHours() * 60) + date.getMinutes();
  return minutesSinceMidnight >= LIGHT_MODE_START_MINUTES &&
    minutesSinceMidnight < DARK_MODE_START_MINUTES
    ? 'light'
    : 'dark';
};

const getMillisecondsUntilNextModeChange = (date = new Date()) => {
  const minutesSinceMidnight = (date.getHours() * 60) + date.getMinutes();
  const nextChange = new Date(date);

  if (minutesSinceMidnight < LIGHT_MODE_START_MINUTES) {
    nextChange.setHours(5, 30, 0, 0);
  } else if (minutesSinceMidnight < DARK_MODE_START_MINUTES) {
    nextChange.setHours(19, 0, 0, 0);
  } else {
    nextChange.setDate(nextChange.getDate() + 1);
    nextChange.setHours(5, 30, 0, 0);
  }

  return Math.max(nextChange.getTime() - date.getTime(), 1000);
};

const getInitialColorModeState = () => {
  try {
    const storedMode = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    const hasUserOverride = window.localStorage.getItem(COLOR_MODE_OVERRIDE_STORAGE_KEY) === 'true';
    if (hasUserOverride && (storedMode === 'light' || storedMode === 'dark')) {
      return { mode: storedMode, hasUserOverride: true };
    }
  } catch (error) {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }

  return { mode: getScheduledColorMode(), hasUserOverride: false };
};

export const createMortyTheme = (mode) => {
  const isDark = mode === 'dark';
  const background = isDark
    ? { default: '#0b1117', paper: '#121a22' }
    : { default: '#f8fafc', paper: '#ffffff' };

  return createTheme({
    palette: {
      mode,
      primary: {
        main: isDark ? '#69c399' : '#4aa378',
        light: isDark ? '#91d8b6' : '#72bb96',
        dark: isDark ? '#459e72' : '#357d5b',
        contrastText: '#07110c',
      },
      secondary: {
        main: isDark ? '#ff936f' : '#ee7850',
      },
      background,
      text: {
        primary: isDark ? '#edf4f7' : '#17212b',
        secondary: isDark ? '#a9b8c3' : '#5f6b76',
        disabled: isDark ? '#71808b' : '#8b96a1',
      },
      divider: isDark ? 'rgba(210, 227, 236, 0.16)' : 'rgba(15, 23, 42, 0.12)',
      action: {
        hover: isDark ? 'rgba(237, 244, 247, 0.08)' : 'rgba(15, 23, 42, 0.05)',
        selected: isDark ? 'rgba(105, 195, 153, 0.16)' : 'rgba(74, 163, 120, 0.12)',
        disabledBackground: isDark ? 'rgba(237, 244, 247, 0.08)' : 'rgba(15, 23, 42, 0.08)',
      },
      success: { main: isDark ? '#58c98d' : '#2e7d32' },
      warning: { main: isDark ? '#ffb35c' : '#ed6c02' },
      error: { main: isDark ? '#ff7373' : '#d32f2f' },
      info: { main: isDark ? '#70b7ff' : '#1976d2' },
    },
    shape: {
      borderRadius: 10,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { backgroundColor: background.default },
          body: {
            backgroundColor: background.default,
            color: isDark ? '#edf4f7' : '#17212b',
            transition: 'background-color 180ms ease, color 180ms ease',
          },
          'input[type=number]::-webkit-outer-spin-button, input[type=number]::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
          'input[type=number]': {
            MozAppearance: 'textfield',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          color: 'inherit',
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderColor: theme.palette.divider,
          }),
          head: ({ theme }) => ({
            backgroundColor: isDark
              ? alpha(theme.palette.common.white, 0.045)
              : alpha(theme.palette.common.black, 0.025),
            color: theme.palette.text.primary,
            fontWeight: 650,
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: isDark ? alpha(theme.palette.common.white, 0.025) : 'transparent',
          }),
          notchedOutline: ({ theme }) => ({
            borderColor: theme.palette.divider,
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: '0.78rem',
          },
        },
      },
    },
  });
};

export function MortyThemeProvider({ children }) {
  const [colorModeState, setColorModeState] = useState(getInitialColorModeState);
  const { mode, hasUserOverride } = colorModeState;

  const toggleColorMode = useCallback(() => {
    setColorModeState((currentState) => ({
      mode: currentState.mode === 'dark' ? 'light' : 'dark',
      hasUserOverride: true,
    }));
  }, []);

  const contextValue = useMemo(
    () => ({ mode, toggleColorMode }),
    [mode, toggleColorMode]
  );
  const theme = useMemo(() => createMortyTheme(mode), [mode]);

  useEffect(() => {
    if (hasUserOverride) return undefined;

    let timeoutId;

    const scheduleNextModeChange = () => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        setColorModeState((currentState) => currentState.hasUserOverride
          ? currentState
          : { ...currentState, mode: getScheduledColorMode() });
        scheduleNextModeChange();
      }, getMillisecondsUntilNextModeChange());
    };

    const syncScheduledMode = () => {
      setColorModeState((currentState) => currentState.hasUserOverride
        ? currentState
        : { ...currentState, mode: getScheduledColorMode() });
      scheduleNextModeChange();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') syncScheduledMode();
    };

    scheduleNextModeChange();
    window.addEventListener('focus', syncScheduledMode);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('focus', syncScheduledMode);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hasUserOverride]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = mode;
    root.style.colorScheme = mode;

    const themeColor = mode === 'dark' ? '#0b1117' : '#f8fafc';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', themeColor);

    if (hasUserOverride) {
      try {
        window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
        window.localStorage.setItem(COLOR_MODE_OVERRIDE_STORAGE_KEY, 'true');
      } catch (error) {
        // The visual preference still works for this session without storage.
      }
    }
  }, [hasUserOverride, mode]);

  return (
    <ColorModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export function useColorMode() {
  const context = useContext(ColorModeContext);
  if (!context) {
    throw new Error('useColorMode must be used within MortyThemeProvider');
  }

  return context;
}
